import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { PLATFORM_CONTACT } from '../config/publicPlatformContact';
import { resolvePublicSocialLinks, type PublicSocialLink } from '../config/publicSocialLinks';
import { fetchPublicBranding } from '../services/api';

export type PublicBrandingState = {
  appName: string;
  slogan: string;
  logoUrl: string | null;
  supportEmail: string;
  supportPhone: string;
  socialLinks: PublicSocialLink[];
  loading: boolean;
};

type PublicBrandingContextValue = PublicBrandingState & {
  refresh: () => Promise<void>;
};

const DEFAULT_STATE: PublicBrandingState = {
  appName: PLATFORM_CONTACT.appName,
  slogan: '',
  logoUrl: null,
  supportEmail: PLATFORM_CONTACT.supportEmailDefault,
  supportPhone: PLATFORM_CONTACT.supportPhone,
  socialLinks: [],
  loading: true,
};

const PublicBrandingContext = createContext<PublicBrandingContextValue | null>(null);

const invalidateListeners = new Set<() => void>();

export function invalidatePublicBranding() {
  invalidateListeners.forEach((listener) => listener());
}

function mapBrandingResponse(data: {
  appName?: string | null;
  slogan?: string | null;
  logoUrl?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  socialLinks?: PublicSocialLink[];
}): PublicBrandingState {
  return {
    appName: data?.appName?.trim() || PLATFORM_CONTACT.appName,
    slogan: data?.slogan?.trim() ?? '',
    logoUrl: data?.logoUrl?.trim() ? data.logoUrl.trim() : null,
    supportEmail: data?.supportEmail?.trim() || PLATFORM_CONTACT.supportEmailDefault,
    supportPhone: data?.supportPhone?.trim() || PLATFORM_CONTACT.supportPhone,
    socialLinks: resolvePublicSocialLinks(data?.socialLinks),
    loading: false,
  };
}

export function PublicBrandingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PublicBrandingState>(DEFAULT_STATE);

  const load = useCallback(async () => {
    try {
      const data = await fetchPublicBranding();
      setState(mapBrandingResponse(data));
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const listener = () => {
      void load();
    };
    invalidateListeners.add(listener);
    return () => {
      invalidateListeners.delete(listener);
    };
  }, [load]);

  const value = useMemo<PublicBrandingContextValue>(
    () => ({
      ...state,
      refresh: load,
    }),
    [state, load],
  );

  return <PublicBrandingContext.Provider value={value}>{children}</PublicBrandingContext.Provider>;
}

export function usePublicBrandingContext(): PublicBrandingContextValue {
  const ctx = useContext(PublicBrandingContext);
  if (!ctx) {
    throw new Error('usePublicBrandingContext must be used within PublicBrandingProvider');
  }
  return ctx;
}
